import { OkrMethodologyFacade } from "@gtmhub/goals/components/facades/okr-methodology.facade";
import { IQService, ITimeoutService, IPromise, IScope, IFilterService } from "angular";
import { StateService } from "@uirouter/angularjs";
import { IBaseGoal, IOkrGridColumn } from "../models";
import { IGtmhubRootScopeService } from "@gtmhub/models";
import { IAssignee, IAssigneesStoreState, AssigneeActions } from "@gtmhub/assignees";
import { IPlanningSession, ISessionsStoreState, PlanningSessionsActions } from "@gtmhub/sessions";
import { ICurrentEmployeeStoreState, CurrentEmployeeActions } from "@gtmhub/employees";
import { GoalService } from "../goal.service";
import { GoalsTreeService } from "../goalsTreeService";
import { PermissionsService } from "@gtmhub/users/permissions-service";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { IStateInit } from "@gtmhub/core/routing";
import { MetricService } from "@gtmhub/okrs/metrics";
import { IGoalsMetricsData } from "@gtmhub/sockets";
import { CustomFieldActions, ICustomFieldsStoreState, ICustomFieldsAllTypesMap, CfMap } from "@gtmhub/customFields";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { INgRedux } from "@gtmhub/state-management";
import { ITeamsSelectedEventArgs, TeamEvents } from "../../teams/events";
import { getOkrGridColumns, convertColumnToColumnDefinition, convertColumnToSortingDefinition, buildParentChildrenIdMap } from "../utils";
import {
  goalsFilterDefinitions,
  processedFiltersForSessionlessGoals,
  filtersOverrideForSessionlessGoals,
  generateDefaultFilters,
  goalsGridPersistedScreenPath,
  goalsPersistedScreenPath,
  goalsViewOptionsPath,
  filtersOverrideForTagTitle,
  filtersOverrideForOkrName,
  separateDynamicValuesForAllGroups,
  hasOnlyHostSessionFilterInAllGroups,
  hasFiltersGroupsWithValues,
} from "../filter-utils";
import { FeatureToggleService } from "@gtmhub/featureToggle";
import {
  IGridSortingRule,
  IGridColumn,
  GridFilterDefinitions,
  GridSortingDefinitions,
  GridColumnDefinitions,
  toGridDefinitionMap,
  toRestLayerRequest,
  RestLayerFilterFactory,
  IGridFiltersChange,
  GridFilterOperator,
  createFilter,
  getPersistedScreenSettings,
  setPersistedScreenSettings,
  IArrayGridFilter,
  OrFiltersGroup,
  removeFiltersByFieldNameAndEmptyGroups,
  IGridFilter,
} from "@gtmhub/shared/components/grid-filtering-bar";
import { localize } from "@gtmhub/localization";
import { NameMap, toKeyMap, pushOrReplace, IdMap, unique } from "@gtmhub/util";
import { IRestLayerRequest, RestLayerFilter } from "@gtmhub/core";
import { ITeamsStoreState, TeamsActions } from "@gtmhub/teams";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";
import { DocumentExporterService } from "@gtmhub/document-exporter/document-exporter-service";
import { triggerFileDownload } from "@gtmhub/shared/dom";
import dayjs from "@gtmhub/dayjs";
import { resetPersistedExpandedItems } from "@gtmhub/okrs/alignment-tree";
import { IItemWorkflow } from "@webapp/okrs/goals/models/goal-workflow.models";
import { filterFieldNames } from "@gtmhub/goals/filter-utils";
import { OkrGridContributor } from "@gtmhub/goals/components/okr-grid-contributors/okr-grid-contributors.component";
import { IMetricChangedEventArgs, OkrEvents } from "@gtmhub/okrs";
import { FeatureFlag } from "@gtmhub/featureToggle/models";
import { AttainmentTypeString } from "@webapp/okrs/goals/models/goal.models";
import { IColoringSetup, ProgressColoring } from "@gtmhub/configuration";
import { AccountService } from "@gtmhub/accounts/accounts-service";
import { AnalyticsService } from "@gtmhub/core/analytics";
import { PlanningSessionEvents } from "@gtmhub/sessions/services/events";

export const MISSING_SESSION_ID_STATE_PARAM = "none";
const PAGING_SIZE = 50;
const FIRST_PAGE = 1;

export interface IAllGoal extends IBaseGoal {
  metrics: IAllMetric[];
  parentId: string;
  ownerIds: string[];
  description: string;
  childrenCount: number;
  metricsCount: number;
  attainment: number;
  fullAggregatedAttainment: number;
  sessionId: string;
  customFields: CfMap;
  obfuscated: boolean;
  private: boolean;
  workflow: IItemWorkflow;
  tags: IAllGoalTag[];
  attainmentTypeString: AttainmentTypeString;
  orderId?: number;
}

export interface IAllMetric {
  id: string;
  name: string;
  ownerIds: string[];
  confidence?: IAllConfidence;
  attainment: number;
  orderId: number;
}

export interface IAllConfidence {
  value: number;
  reason: string;
}

interface IAllGoalTag {
  title: string;
}

interface IAllGoalsRedux {
  sessionsIdMap: Record<string, IPlanningSession>;
  sessionsFetched: boolean;
  sessionsError: IUIError;
  assigneesIdMap: IdMap<IAssignee>;
  assigneesFetched: boolean;
  assigneesError: IUIError;
  teamsFetched: boolean;
  teamsError: IUIError;
  customFields: ICustomFieldsAllTypesMap;
  customFieldsFetched: boolean;
  customFieldsError: IUIError;
}

const bindStateToScope = (
  state: ITeamsStoreState & IAssigneesStoreState & ISessionsStoreState & ICustomFieldsStoreState & ICurrentEmployeeStoreState
): IAllGoalsRedux => {
  const { map: sessionsIdMap, isFetched: sessionsFetched, error: sessionsError } = state.sessions;
  const { map: assigneesIdMap, isFetched: assigneesFetched, error: assigneesError } = state.assignees;
  const { isFetched: teamsFetched, error: teamsError } = state.teams;
  const { map: customFields, isFetched: customFieldsFetched, error: customFieldsError } = state.customFields;

  return {
    sessionsIdMap,
    sessionsFetched,
    sessionsError,
    assigneesIdMap,
    assigneesFetched,
    assigneesError,
    teamsFetched,
    teamsError,
    customFields,
    customFieldsFetched,
    customFieldsError,
  };
};

export interface IAllGoalsScope extends IAllGoalsRedux, IScope, IExtendedRootScopeService {
  isModuleTagsEnabled: boolean;
  isDesignScoreGridEnabled: boolean;
  session: IPlanningSession;
  goals: IAllGoal[];
  indicators: {
    loading?: boolean;
    reloading?: boolean;
  };
  date: Date;
  workflowEnabled: boolean;
  allowedActions: string[];
  columns: IOkrGridColumn[];
  filterDefinitions: GridFilterDefinitions;
  filters: OrFiltersGroup[];
  sortingDefinitions: GridSortingDefinitions;
  sortingRules: IGridSortingRule[];
  columnDefinitions: GridColumnDefinitions;
  gridColumns: IGridColumn[];
  totalPages: number;
  currentPage: number;

  onPageChange(page: number): void;

  showEmptyBlock: boolean;
  showNoFilterResults: boolean;
  parentChildrenIdMap: IdMap<OkrGridContributor[]>;

  isProgressAggregationDisabledForSession(sessionId: string): boolean;

  filtersChange(change: IGridFiltersChange): void;

  sortingChange(): void;

  columnsChange(): void;

  filterByTag(tag: string): void;

  tag(item: IAllGoalTag, subject: IAllGoal): void;

  removeTag(goal: IAllGoal, tag: IAllGoalTag): void;

  openGoal(id: string): void;

  deleteGoal(id: string): void;

  deleteMetric(goalId: string, metricId: string): void;

  deleteContributor(contributor: OkrGridContributor, goal: IAllGoal): void;

  changeAssignee(item: IAssignee, subject: IAllGoal | IAllMetric, subjectType: string): void;

  checkTasks(fieldName: string, goal: IAllGoal): void;

  exportToExcel(): void;

  getCustomFieldForGoal(goal: IAllGoal, fieldName: string): unknown;
}

export interface IAllGoalsStateParams {
  page?: string;
  planningSessionId?: string;
}

const gridScreenName = "grid";

export class AllGoalsCtrl implements IStateInit {
  private teamEvents: TeamEvents;
  private okrEvents: OkrEvents;
  private sessionEvents: PlanningSessionEvents;
  private columnsFieldMap: NameMap<IOkrGridColumn>;
  private goalsTotalCount: number;
  private isDesignScoreGridEnabled = false;

  public static $inject = [
    "$q",
    "$rootScope",
    "$scope",
    "$state",
    "$stateParams",
    "$timeout",
    "GoalService",
    "GoalsTreeService",
    "MetricService",
    "PermissionsService",
    "DocumentExporterService",
    "UIErrorHandlingService",
    "CustomFieldActions",
    "$ngRedux",
    "PlanningSessionsActions",
    "CurrentEmployeeActions",
    "AssigneeActions",
    "TeamsActions",
    "$filter",
    "FeatureToggleService",
    "OkrMethodologyFacade",
    "ProgressColoring",
    "AccountService",
    "AnalyticsService",
  ];

  constructor(
    private $q: IQService,
    private $rootScope: ITraceRootScopeService & IGtmhubRootScopeService,
    private $scope: IAllGoalsScope & IExtendedRootScopeService,
    private $state: StateService,
    private $stateParams: IAllGoalsStateParams,
    private $timeout: ITimeoutService,
    private goalService: GoalService,
    private goalsTreeService: GoalsTreeService,
    private metricService: MetricService,
    private permissionsService: PermissionsService,
    private documentExporterService: DocumentExporterService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private customFieldsActions: CustomFieldActions,
    private $ngRedux: INgRedux,
    private planningSessionsActions: PlanningSessionsActions,
    private currentEmployeeActions: CurrentEmployeeActions,
    private assigneeActions: AssigneeActions,
    private teamsActions: TeamsActions,
    private $filter: IFilterService,
    private featureToggleService: FeatureToggleService,
    private okrMethodologyFacade: OkrMethodologyFacade,
    private progressColoring: ProgressColoring,
    private accountService: AccountService,
    private analyticsService: AnalyticsService
  ) {
    this.$rootScope.noNavigation = false;
    this.teamEvents = new TeamEvents(this.$scope);
    this.okrEvents = new OkrEvents(this.$scope);
    this.sessionEvents = new PlanningSessionEvents(this.$scope);
  }

  public stateInit(): IPromise<unknown> {
    //set to an empty array to override the value that is present in $rootScope after visiting an insight
    //until the data is fetched and the proper intialization is conducted
    this.$scope.filters = undefined;
    this.$scope.indicators = { loading: true };

    this.$scope.$on("$destroy", this.$ngRedux.connect(bindStateToScope)(this.$scope));
    this.$scope.$on("tagsChanged", () => this.getGoals());

    this.$ngRedux.dispatch(this.currentEmployeeActions.fetchCurrentEmployeeIfMissing());
    this.$ngRedux.dispatch(this.teamsActions.fetchTeamsIfMissing());
    this.$ngRedux.dispatch(this.customFieldsActions.getCustomFields());
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
    this.$ngRedux.dispatch(this.planningSessionsActions.getSessionsIfMissing());

    //TODO: improve init
    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.assigneesFetched && this.$scope.customFieldsFetched,
        () => this.$scope.assigneesError || this.$scope.customFieldsError
      )
      .then(() => {
        return this.$q
          .all({
            isDesignScoreGridEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.DesignScoreGrid),
          })
          .then(({ isDesignScoreGridEnabled }) => {
            this.isDesignScoreGridEnabled = isDesignScoreGridEnabled;
          });
      })
      .then(
        () => {
          return this.init();
        },
        (error) => {
          this.catchError(error);
        }
      );
  }

  private init = (): IPromise<unknown> => {
    this.setColumnsAndSortingDefinitions();
    this.setColoringScale();
    this.$scope.filterDefinitions = goalsFilterDefinitions({
      customFields: this.$scope.customFields,
      planningSessionId: this.$stateParams.planningSessionId,
      isNestedView: null,
      isDesignScoreGridEnabled: this.isDesignScoreGridEnabled,
    });

    this.$scope.onPageChange = this.onPageChange;

    const paramsPage = Number(this.$stateParams.page);
    this.$scope.currentPage = isNaN(paramsPage) ? FIRST_PAGE : paramsPage;

    this.$rootScope.ui = {};
    this.$scope.filtersChange = this.filtersChange;
    this.$scope.sortingChange = this.sortingChange;
    this.$scope.columnsChange = this.columnsChange;
    this.$scope.exportToExcel = this.exportToExcel;

    this.$scope.removeTag = this.removeTag;

    this.$scope.changeAssignee = this.changeAssignee;
    this.$scope.tag = this.tag;

    this.$scope.deleteGoal = this.deleteGoal;
    this.$scope.deleteMetric = this.deleteMetric;
    this.$scope.deleteContributor = this.deleteContributor;

    this.$scope.filterByTag = this.filterByTag;
    this.$scope.checkTasks = this.checkTasks;
    this.$scope.openGoal = this.openGoal;
    this.$scope.getCustomFieldForGoal = this.getCustomFieldForGoal;
    this.$scope.isProgressAggregationDisabledForSession = this.isProgressAggregationDisabledForSession;

    this.teamEvents.onTeamsSelected((args) => this.onTeamsSelected(args));
    this.okrEvents.onGoalsChanged((args) => args.reason === "goalChildReordered" && this.onGoalsChanged());
    this.okrEvents.onGoalsChanged((args) => args.reason === "metricCreated" && this.onMetricCreated(args.detail));
    this.$scope.$on("goalsChanged", this.onGoalsChanged);
    this.$scope.$on("goalDeleted", this.onGoalDeleted);
    this.$scope.$on("gridColumnsChange", this.setColumnsAndSortingDefinitions);

    this.$scope.date = new Date();
    this.$scope.$on("wsGoalsAndMetricsChanged", this.updateGoalsTree);
    const initPromises: IPromise<unknown>[] = [];

    if (this.$stateParams.planningSessionId !== MISSING_SESSION_ID_STATE_PARAM) {
      initPromises.push(
        this.$scope
          .watchUntilAndRejectOnError(
            () => this.$scope.sessionsFetched,
            () => this.$scope.sessionsError
          )
          .then(
            () => {
              this.$scope.session = this.$scope.sessionsIdMap[this.$stateParams.planningSessionId] || null;
              this.$scope.workflowEnabled = this.checkSessionWorkflow(this.$scope.session);

              if (!this.$scope.session) {
                this.$scope.indicators.loading = false;
                return;
              }

              this.sessionEvents.broadcastSessionVisited(this.$scope.session);
              return this.$q
                .all({
                  actions: this.permissionsService.getCurrentUserAllowedActionsPerItem(this.$scope.session.id, "session"),
                  isModuleTagsEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.ModuleTags),
                  isDesignScoreGridEnabled: this.featureToggleService.isFeatureAvailable(FeatureFlag.DesignScoreGrid),
                })
                .then(({ actions, isModuleTagsEnabled, isDesignScoreGridEnabled }) => {
                  this.$scope.allowedActions = actions.currentUserActions;
                  this.$scope.isModuleTagsEnabled = isModuleTagsEnabled;
                  this.$scope.isDesignScoreGridEnabled = isDesignScoreGridEnabled;
                  this.initFilters();
                });
            },
            () => this.uiErrorHandlingService.handleModal(this.$scope.sessionsError)
          )
      );
    } else {
      /** init the filters when browsing 'goals without sessions' - otherwise the screen appears to be blank-white */
      this.initFilters();
    }

    return this.$q.all(initPromises);
  };

  private setColumnsAndSortingDefinitions = (): void => {
    // TODO: remove filter when all users settings are patched
    this.$scope.columns = getOkrGridColumns(goalsViewOptionsPath, this.$scope.customFields.goal || [], this.isDesignScoreGridEnabled).filter(
      (col) => col.field !== "metricsCount"
    );

    const visibleColumnsOnly = (column: IOkrGridColumn): boolean => column.visible;

    // assignments below can be done in 1 iteration, but columns count is limited and readability is better if done in multiple iterations
    // the map below always holds full set of available columns
    // on columns change this.$scope.columns will be missing all values with visible: false
    this.columnsFieldMap = toKeyMap("field", this.$scope.columns);
    this.$scope.columnDefinitions = toGridDefinitionMap(this.$scope.columns.map((c) => convertColumnToColumnDefinition(c, this.$filter)));
    this.$scope.gridColumns = this.$scope.columns.filter(visibleColumnsOnly).map((column) => ({ fieldName: column.field }));
    this.$scope.sortingDefinitions = this.createSortingDefinitionsMap();
  };

  private createSortingDefinitionsMap = () => {
    return toGridDefinitionMap(
      this.$scope.columns
        .filter((col) => col.field !== "krs" && col.field !== "krsAndChildObjs" && col.visible && col.isSortable)
        .map((c) => convertColumnToSortingDefinition(c, this.$filter))
        .concat({
          displayName: localize("number_of_key_results_cap"),
          fieldName: "metricsCount",
          trackingName: "Metrics count",
        })
    );
  };

  private checkSessionWorkflow = (session: IPlanningSession): boolean => {
    if (session && session.workflow && session.workflow.reviewers && session.workflow.reviewers > 0) {
      return true;
    }

    return false;
  };

  private catchError = (error): void => {
    if (this.$scope.indicators.loading) {
      this.uiErrorHandlingService.handleIndicator(error, { indicator: "loading" });
    } else {
      this.$scope.indicators.reloading = false;
      this.uiErrorHandlingService.handleModal(error);
    }
  };

  private initFilters = () => {
    let persistedFilters: OrFiltersGroup[] = (getPersistedScreenSettings(this.screenFiltersPath(), "filters") || []).map(
      (filtersGroup) =>
        (filtersGroup = filtersGroup.map((filter) =>
          filter.fieldName === "ownerId" ? { ...filter, fieldName: "ownerIds", operator: "contains" as GridFilterOperator } : filter
        ))
    );

    if (!this.$scope.isModuleTagsEnabled) {
      delete this.$scope.filterDefinitions["tags.title"];
      persistedFilters = removeFiltersByFieldNameAndEmptyGroups(persistedFilters, "tags.title");
    }

    if (persistedFilters.length) {
      this.$scope.filters = persistedFilters;
      this.generateSortingRules();
    } else {
      this.setDefaultUnifiedFilteringBarOptions();
    }
  };

  private screenFiltersPath = () => goalsGridPersistedScreenPath(this.$stateParams.planningSessionId);

  private generateSortingRules = (): void => {
    const persistedSorting = getPersistedScreenSettings(this.screenSortingPath(), "sorting");

    if (persistedSorting) {
      this.$scope.sortingRules = persistedSorting.filter((sortingRule) => sortingRule.fieldName in this.$scope.sortingDefinitions);
    } else {
      // TODO: discuss with UX whether to create sorting definitions for invisible columns
      const attainmentColumnSelected = this.$scope.gridColumns.find((column) => column.fieldName === "attainment");
      this.$scope.sortingRules = attainmentColumnSelected ? [{ fieldName: "attainment", reversed: false }] : [];
    }
  };

  public getCustomFieldForGoal = (goal: IAllGoal, fieldName: string) => {
    return goal.customFields?.[fieldName];
  };

  private screenSortingPath = () => goalsPersistedScreenPath(this.$stateParams.planningSessionId, gridScreenName);

  private setDefaultUnifiedFilteringBarOptions = (): void => {
    const defaultFilters = generateDefaultFilters(this.$stateParams.planningSessionId);
    this.$scope.filters = this.$scope.isModuleTagsEnabled ? defaultFilters : removeFiltersByFieldNameAndEmptyGroups(defaultFilters, "tags.title");
    this.generateSortingRules();
  };

  private filtersChange = (change: IGridFiltersChange): void => {
    this.$rootScope.traceAction("change_filters", () => {
      if (change.initial) {
        this.setCurrentPage(this.$scope.currentPage);
      } else {
        if (this.$scope.session && this.$scope.session.id !== MISSING_SESSION_ID_STATE_PARAM) {
          // filters are shared between goals views
          // when changing filters in an existing session the expanded collection should be wiped so tree views should look as expected when visited
          resetPersistedExpandedItems(this.$scope.session.id);
        }
        setPersistedScreenSettings(this.screenFiltersPath(), "filters", this.$scope.filters);
        this.setCurrentPage(FIRST_PAGE);
      }
    });
  };

  private sortingChange = (): void => {
    this.$rootScope.traceAction("change_sorting", () => {
      setPersistedScreenSettings(this.screenSortingPath(), "sorting", this.$scope.sortingRules);
      this.setCurrentPage(FIRST_PAGE);
    });
  };

  private columnsChange = (): void => {
    this.$rootScope.traceAction("change_columns", () => {
      this.$scope.columns = this.$scope.gridColumns.map((gridColumn) => ({
        ...this.columnsFieldMap[gridColumn.fieldName],
        visible: true,
      }));
      const persistedSharedViewOptions = getPersistedScreenSettings(goalsViewOptionsPath, "sharedViewOptions") || {};
      persistedSharedViewOptions.okrGridColumns = this.$scope.columns;
      setPersistedScreenSettings(goalsViewOptionsPath, "sharedViewOptions", persistedSharedViewOptions);
      this.$scope.sortingDefinitions = this.createSortingDefinitionsMap();

      if (this.areSortingRuleMissingFromSortingDefinitions()) {
        this.generateSortingRules();
        this.sortingChange();
      }
    });
  };

  private areSortingRuleMissingFromSortingDefinitions = (): boolean => {
    const rulesNotInDefinitions = this.$scope.sortingRules.filter((sortingRule) => !(sortingRule.fieldName in this.$scope.sortingDefinitions));
    return rulesNotInDefinitions.length > 0;
  };

  private setTotalPages = (totalCount: number) => {
    this.$scope.totalPages = Math.ceil(totalCount / PAGING_SIZE);

    if (this.$scope.totalPages && this.$scope.totalPages < this.$scope.currentPage) {
      this.setCurrentPage(this.$scope.totalPages);
    }
  };

  private setCurrentPage = (page?: number) => {
    this.$scope.currentPage = page || FIRST_PAGE;
    this.goToCurrentPage();
  };

  private goToCurrentPage = () => {
    this.$state.go(".", { page: this.$scope.currentPage }, { notify: false });
    this.getGoals();
  };

  private onPageChange = (page: number) => {
    this.$scope.currentPage = page;
    this.$rootScope.traceAction("change_page", () => {
      this.goToCurrentPage();
    });
  };

  // to do: reuse logic from users screen when working on https://gtmhub.atlassian.net/browse/GVS-15306
  private filterByTag = (tag: string): void => {
    this.$rootScope.traceAction("filter_by_tag", () => {
      this.$scope.filters = this.$scope.filters.map((filtersGroup) => {
        return this.createFilterGroupByTag(filtersGroup, tag);
      });

      this.trackFiltersChangedExternally({
        source: "OKR Tag Label",
        fieldName: filterFieldNames.tags,
        isAdding: true,
      });
    });
  };

  private createFilterGroupByTag = (filtersGroup: OrFiltersGroup, tag: string): IGridFilter[] => {
    const hasTagFilters = filtersGroup.some((filter) => filter.fieldName === filterFieldNames.tags);

    if (!hasTagFilters) {
      const tagGridFilter = createFilter(this.$scope.filterDefinitions[filterFieldNames.tags]);
      tagGridFilter.value = [tag];
      return pushOrReplace(filtersGroup, tagGridFilter);
    }

    return filtersGroup.map((filter) => {
      if (filter.fieldName === filterFieldNames.tags) {
        const tagGridFilter = createFilter(this.$scope.filterDefinitions[filterFieldNames.tags]);
        tagGridFilter.value = unique([...(filter.value as string), tag]);
        return tagGridFilter;
      }
      return filter;
    });
  };

  private trackFiltersChangedExternally = ({ source, fieldName, isAdding }: { source: string; fieldName: string; isAdding: boolean }) => {
    this.analyticsService.track("Filter Changed Externally", {
      source: source,
      field_name: fieldName,
      is_adding: isAdding,
    });
  };

  private tag = (item: IAllGoalTag, subject: IAllGoal) => {
    this.$rootScope.traceAction("tag_goal", () => {
      const tagAlreadyAdded = subject.tags.some((goalTag) => goalTag.title === item.title);
      if (tagAlreadyAdded) {
        return;
      }

      this.goalService.addTagToGoal(item.title, subject.id).then(
        () => {
          this.getGoals();
        },
        (err) => this.uiErrorHandlingService.handleModal(err)
      );
    });
  };

  private removeTag = (goal: IAllGoal, tag: IAllGoalTag) => {
    this.$rootScope.traceAction("remove_tag_from_goal", () => {
      this.goalService.removeTagFromGoal(tag, goal.id);

      this.$scope.indicators.reloading = true;

      this.$timeout(() => {
        this.getGoals();
      }, 400);
    });
  };

  private getGoals = (): IPromise<unknown> => {
    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.teamsFetched,
        () => this.$scope.teamsError
      )
      .then(() => {
        return this.initializeGoals();
      })
      .catch((error) => {
        this.catchError(error);
      });
  };

  private initializeGoals = () => {
    const skip = (this.$scope.currentPage - 1) * PAGING_SIZE;
    const goalsRequest = this.initializeGoalsRequest();

    goalsRequest.skip = skip;
    goalsRequest.limit = PAGING_SIZE;
    goalsRequest.fields =
      `metrics{id,confidence,name,attainment,ownerIds,manualType,lastCheckInDate,cascadeType,links,orderId},` +
      `parentId,ownerIds,description,name,dateFrom,dateTo,childrenCount,fullSubTreeCount,metricsCount,` +
      "attainment,fullAggregatedAttainment,sessionId,customFields,obfuscated,private,workflow{status},locked," +
      "tags{id,name,title,isActive,itemsTaggedCount},attainmentTypeString,designScore{totalPoints}";

    this.$scope.indicators.reloading = true;

    return this.onGetGoalsV2(goalsRequest);
  };

  private initializeGoalsRequest = (): IRestLayerRequest => {
    const goalsWithoutSessionAreDisplayed = this.$stateParams.planningSessionId === MISSING_SESSION_ID_STATE_PARAM;

    // copying filters as we have to modify values passed to generate rest layer request but we don't want to modify component values
    let filters = angular.copy(this.$scope.filters);
    let filtersOverride: Record<string, RestLayerFilterFactory> = null;
    // to do: remove this as well as related variables and util methods when we implement non-existent dynamic filter
    // https://gtmhub.atlassian.net/browse/GVS-2095
    if (goalsWithoutSessionAreDisplayed) {
      filters = filters.map((filtersGroup) => processedFiltersForSessionlessGoals(filtersGroup));
      filtersOverride = filtersOverrideForSessionlessGoals();
    }

    filtersOverride = { ...filtersOverride, ...filtersOverrideForTagTitle(), ...filtersOverrideForOkrName() };

    const separatedFilterValues: OrFiltersGroup[] = separateDynamicValuesForAllGroups(filters);
    return toRestLayerRequest({ filtersOverride, filters: separatedFilterValues, sorting: this.$scope.sortingRules });
  };

  private onGetGoalsV2 = (goalsRequest: IRestLayerRequest) => {
    return this.goalService.getGoalsV2<IAllGoal>(goalsRequest).then((result) => {
      this.goalsTotalCount = result.totalCount;
      this.setTotalPages(this.goalsTotalCount);

      this.$scope.indicators.loading = false;
      this.$scope.indicators.reloading = false;
      this.$scope.goals = result.items || [];

      this.setEmptyScreenAndNoFilterDisplay();

      this.getGoalsChildren();
    });
  };

  private setEmptyScreenAndNoFilterDisplay = (): void => {
    const anyFilterWithValuesInGroups = hasFiltersGroupsWithValues(this.$scope.filters);
    const onlyHostSessionFilterInAllGroups = hasOnlyHostSessionFilterInAllGroups(this.$scope.filters, this.$stateParams.planningSessionId);

    this.$scope.showEmptyBlock = this.noGoalsFetched() && (!anyFilterWithValuesInGroups || onlyHostSessionFilterInAllGroups);
    this.$scope.showNoFilterResults = this.noGoalsFetched() && anyFilterWithValuesInGroups && !onlyHostSessionFilterInAllGroups;
  };

  private noGoalsFetched = (): boolean => !this.$scope.indicators.loading && !this.$scope.indicators.reloading && this.goalsTotalCount === 0;

  private getGoalsChildren = (): void => {
    const goalsChildrenRequestParams = this.buildGoalsChildrenQuery();

    if (goalsChildrenRequestParams) {
      this.goalService.getGoalsV2<IAllGoal>(goalsChildrenRequestParams).then((result) => {
        this.$scope.parentChildrenIdMap = buildParentChildrenIdMap(this.$scope.goals, result.items);
      });
      return;
    }

    this.$scope.parentChildrenIdMap = buildParentChildrenIdMap(this.$scope.goals);
  };

  private buildGoalsChildrenQuery = (): IRestLayerRequest => {
    const parentGoalsIds = this.$scope.goals.filter((goal) => goal.childrenCount > 0 && goal.attainmentTypeString !== "average_kr").map((goal) => goal.id);

    if (!parentGoalsIds.length) {
      return;
    }

    const filters: RestLayerFilter[] = [];
    filters.push({ parentId: { $in: parentGoalsIds } });

    const sessionIdsInFilters = this.$scope.filters.reduce((sessionIds: string[], filtersGroup: OrFiltersGroup): string[] => {
      const sessionFilter = filtersGroup.find((f) => f.fieldName === "sessionId");
      if (sessionFilter && sessionFilter.value) {
        sessionIds.push(...(<IArrayGridFilter>sessionFilter).value);
      }
      return sessionIds;
    }, []);

    if (sessionIdsInFilters.length) {
      filters.push({ sessionId: { $in: [...new Set(sessionIdsInFilters)] } });
    }

    return {
      filter: { $and: filters },
      // we need to take all children goals
      page: FIRST_PAGE,
      limit: -1,
      fields: `parentId,ownerIds,name,attainment,sessionId,obfuscated,orderId`,
    };
  };

  private openGoal = (id: string) => {
    this.$state.go("._goalSidebar.goal", { id: id });
  };

  private deleteGoal = (goalId: string) => {
    this.$rootScope.traceAction("delete_goal", () => {
      this.$scope.indicators.reloading = true;
      this.goalService.deleteGoal(goalId).then(
        () => {
          this.getGoals();
        },
        (err) => {
          this.$scope.indicators.reloading = false;
          this.uiErrorHandlingService.handleModal(err);
        }
      );
    });
  };

  private onGoalDeleted = (evt, id: string): void => {
    for (let i = 0; i < this.$scope.goals.length; i++) {
      if (this.$scope.goals[i].id === id) {
        this.$scope.goals.splice(i, 1);
      }
    }

    if (this.$scope.parentChildrenIdMap[id]) {
      delete this.$scope.parentChildrenIdMap[id];
    }

    Object.entries(this.$scope.parentChildrenIdMap).forEach(([parentId, children]) => {
      this.$scope.parentChildrenIdMap[parentId] = children.filter((child) => child.id !== id);
    });
  };

  private onGoalsChanged = () => {
    if (this.$scope.session && this.$scope.session.id) {
      this.getGoals();
    }
  };

  private onMetricCreated = (detail: IMetricChangedEventArgs["detail"]): void => {
    const goal = this.$scope.goals.find((goal) => goal.id === detail.goalId);

    if (!goal) return;

    if (!goal.metrics) {
      goal.metrics = [<IAllMetric>detail.metric];
    } else {
      goal.metrics.push(<IAllMetric>detail.metric);
    }

    this.$scope.parentChildrenIdMap = buildParentChildrenIdMap(this.$scope.goals);
  };

  private updateGoalsTree = (evt, wsData: IGoalsMetricsData) => {
    if (this.$stateParams.planningSessionId === wsData.sessionId) {
      this.goalsTreeService.updateGoalsTree(wsData, this.$scope.goals);
    }
  };

  private deleteMetric = (goalId: string, metricId: string) => {
    this.$rootScope.traceAction("delete_metric", () => {
      this.metricService.deleteMetric(goalId, metricId).then(() => {
        this.getGoals();
      });
    });
  };

  private deleteContributor = (contributor: OkrGridContributor, goal: IAllGoal): void => {
    if (contributor.childType === "metric") {
      this.deleteMetric(goal.id, contributor.id);
      return;
    }

    this.deleteGoal(contributor.id);
  };

  private changeAssignee = (item: IAssignee, subject: IAllGoal | IAllMetric, subjectType: string) => {
    const areMultipleOwnersPerGoalDisabled = this.okrMethodologyFacade.areMultipleOwnersDisabled("goal", this.$scope.session?.settings);
    const areMultipleOwnersPerMetricDisabled = this.okrMethodologyFacade.areMultipleOwnersDisabled("metric", this.$scope.session?.settings);

    this.$rootScope.traceAction("change_assignee", () => {
      if (subject.ownerIds && subject.ownerIds.includes(item.id)) return;
      let ownerPatch = { ownerIds: [...subject.ownerIds, item.id] };

      if (subjectType === "goal") {
        if (areMultipleOwnersPerGoalDisabled) {
          ownerPatch = { ownerIds: [item.id] };
        }
        this.goalService.patchGoal(subject.id, ownerPatch).then(
          () => {
            this.getGoals();
          },
          (err) => this.uiErrorHandlingService.handleModal(err)
        );
      }

      if (subjectType === "metric") {
        if (areMultipleOwnersPerMetricDisabled) {
          ownerPatch = { ownerIds: [item.id] };
        }

        const patch = {
          id: subject.id,
          ...ownerPatch,
        };

        this.metricService.patchMetric(patch).then(
          () => {
            this.getGoals();
          },
          (err) => this.uiErrorHandlingService.handleModal(err)
        );
      }
    });
  };

  private onTeamsSelected = (params: ITeamsSelectedEventArgs) => {
    if (params.isSingleTeam) {
      setTimeout(() => {
        this.$state.go(
          "gtmhub.goals.all",
          {
            planningSessionId: this.$scope.session.id,
            teamAndMemberIds: params.teams[0].id,
            ownerIds: null,
          },
          {
            reload: true,
          }
        );
      }, 100);
    }
  };

  private checkTasks = (fieldName: string, goal: IAllGoal) => {
    if (goal.customFields && typeof goal.customFields[fieldName] === "undefined") {
      const index = this.$scope.goals
        .map(function (e) {
          return e.id;
        })
        .indexOf(goal.id);
      this.$scope.goals[index].customFields[fieldName] = {
        checked: [],
        tasks: [],
      };
    }
  };

  private exportToExcel = (): void => {
    const filtersOverride = { ...filtersOverrideForTagTitle(), ...filtersOverrideForOkrName() };
    const { filter, sort } = toRestLayerRequest({
      filters: this.$scope.filters,
      sorting: this.$scope.sortingRules,
      filtersOverride: filtersOverride,
    });
    const hostSessionId = this.$scope.session.id;
    const hostSessionTitle = this.$scope.session.title;

    this.documentExporterService.exportGoalsGridToExcel({ filter, sort, hostSessionId }).then(
      (xlsxFileResponse) => {
        const generatedExcel = new Blob([xlsxFileResponse], { type: "application/octet-stream" });
        const fileName = `Gtmhub OKRs.${hostSessionTitle}.${dayjs().format("YYYY-MM-DD")}.xlsx`;
        triggerFileDownload(generatedExcel, fileName);
      },
      (error) => this.uiErrorHandlingService.handleModal(error)
    );
  };

  private isProgressAggregationDisabledForSession = (sessionId: string): boolean => {
    const sessionSettings = this.$scope.sessionsIdMap[sessionId]?.settings;
    return this.okrMethodologyFacade.isProgressAggregationDisabled(sessionSettings);
  };

  private setColoringScale(): void {
    if (!this.progressColoring.getColoring()) {
      const accountColoring = this.accountService.getAccountSetting<IColoringSetup>("coloring");
      this.progressColoring.setColoring(accountColoring);
    }
  }
}
